import React, { useState, useEffect, useMemo } from 'react';
import { Container, Row, Col, Card, Form, Button, Badge, Pagination } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { Download } from 'lucide-react';
import { reportService } from '../services/apiService';
import ReportHeader from '../components/ReportHeader';
import StackedTable from '../components/StackedTable';
import SortableHeader from '../components/SortableHeader';
import PlanFilter, { planKey, planLabel, planOptions, planRank } from '../components/PlanFilter';
import { LoadingState, ErrorState } from '../components/PageState';
import { useAuthToken } from '../hooks/useAuthToken';
import { useIsMobile } from '../hooks/useMediaQuery';
import { EMPTY, formatDate, formatNumber, plural } from '../utils/format';
import { downloadCsv } from '../utils/csv';
import { rowClick } from '../utils/rows';

const PAGE_SIZE = 50;

/** The date the From and To range applies to. */
const DATE_FIELDS = [
  { value: 'companyCreated', label: 'Company created' },
  { value: 'adminSince', label: 'Admin joined' },
];

const ADMIN_JOINED_HINT = "When the admin's account was created. Team Today does not record when someone was made admin.";

const fullName = (admin) => [admin.firstName, admin.lastName].filter(Boolean).join(' ');

/** "2026-08-01" as local midnight, so the day shown never shifts with the time zone. */
const localDay = (iso) => (iso ? new Date(`${iso}T00:00:00`) : null);

/** The value each sortable column sorts by. Blank values always sort last. */
const SORT_VALUES = {
  name: (a) => (fullName(a) || a.email || '').toLowerCase(),
  email: (a) => (a.email || '').toLowerCase(),
  companyName: (a) => (a.companyName || '').toLowerCase(),
  plan: (a) => planRank(planKey(a)),
  paidUsers: (a) => a.paidUsers ?? 0,
  companyUsers: (a) => a.companyUsers ?? 0,
  companyCreated: (a) => a.companyCreated || '',
  adminSince: (a) => a.adminSince || '',
};

const compareBy = (field, direction) => (a, b) => {
  const av = SORT_VALUES[field](a);
  const bv = SORT_VALUES[field](b);
  if ((av === '') !== (bv === '')) return av === '' ? 1 : -1;
  if (av < bv) return direction === 'asc' ? -1 : 1;
  if (av > bv) return direction === 'asc' ? 1 : -1;
  return 0;
};

/** Each company once, from a list of admin rows. */
const uniqueCompanies = (admins) => {
  const byId = new Map();
  admins.forEach((a) => { if (!byId.has(a.companyId)) byId.set(a.companyId, a); });
  return [...byId.values()];
};

/**
 * Every company admin across every company. The whole list loads once and the filters run here:
 * a date range on when the company was created (or when the admin joined), paid or free, and plan.
 * Paid means paid seats, the same rule as the Reports page. The tiles and the CSV follow the filters.
 */
const CompanyAdmins = () => {
  const refreshToken = useAuthToken();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [dateField, setDateField] = useState('companyCreated');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [paid, setPaid] = useState('all');
  const [hiddenPlans, setHiddenPlans] = useState(() => new Set());
  const [sortField, setSortField] = useState('companyCreated');
  const [sortDirection, setSortDirection] = useState('desc');
  const [page, setPage] = useState(1);

  useEffect(() => {
    fetchAdmins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      setError(null);
      await refreshToken();
      const response = await reportService.companyAdmins();
      setAdmins(response.data.admins || []);
    } catch (err) {
      setError('Could not load the company admins.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Every filter change starts again from the first page
  const resetting = (setter) => (value) => { setter(value); setPage(1); };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      // Numbers and dates read best biggest or newest first
      setSortDirection(['paidUsers', 'companyUsers', 'companyCreated', 'adminSince'].includes(field) ? 'desc' : 'asc');
    }
    setPage(1);
  };

  const rangeProblem = from && to && from > to ? 'From is after To' : null;
  const filtersActive = search.trim() || from || to || paid !== 'all' || hiddenPlans.size > 0;

  const clearFilters = () => {
    setSearch('');
    setFrom('');
    setTo('');
    setPaid('all');
    setHiddenPlans(new Set());
    setPage(1);
  };

  // Plan counts are companies, not admins, taken from the whole list so the options never disappear
  const plans = useMemo(() => planOptions(uniqueCompanies(admins)), [admins]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = admins.filter((a) => {
      if (q && ![fullName(a), a.email, a.companyName].some((v) => (v || '').toLowerCase().includes(q))) return false;
      // ISO dates compare as text; an admin with no date drops out once a range is set
      const day = a[dateField];
      if (from && (!day || day < from)) return false;
      if (to && (!day || day > to)) return false;
      if (paid === 'paid' && !(a.paidUsers > 0)) return false;
      if (paid === 'free' && a.paidUsers > 0) return false;
      return !hiddenPlans.has(planKey(a));
    });
    return filtered.sort(compareBy(sortField, sortDirection));
  }, [admins, search, dateField, from, to, paid, hiddenPlans, sortField, sortDirection]);

  const stats = useMemo(() => {
    const companies = uniqueCompanies(rows);
    const paying = companies.filter((c) => c.paidUsers > 0);
    return {
      admins: rows.length,
      companies: companies.length,
      paidCompanies: paying.length,
      paidSeats: paying.reduce((sum, c) => sum + c.paidUsers, 0),
    };
  }, [rows]);

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const sortProps = { sortField, sortDirection, onSort: handleSort };
  const controlStyle = isMobile ? { width: '100%' } : undefined;
  // On phones the date controls take a line each (a date input will not shrink to half the width),
  // with their labels the same width so the three inputs line up
  const dateLabelStyle = isMobile ? { minWidth: 36 } : undefined;

  const exportCsv = () => downloadCsv(
    'company-admins',
    ['first_name', 'last_name', 'email', 'user_id', 'first_user', 'marketing_emails', 'admin_joined',
      'company_id', 'company_name', 'company_enabled', 'plan', 'paid_seats', 'users', 'company_created'],
    rows.map((a) => [
      a.firstName || '', a.lastName || '', a.email || '', a.userId, a.signedUpCompany ? 'yes' : 'no',
      a.marketingEmails ? 'yes' : 'no', a.adminSince || '', a.companyId, a.companyName || '',
      a.companyEnabled ? 'yes' : 'no', planLabel(planKey(a)), a.paidUsers ?? 0, a.companyUsers ?? 0, a.companyCreated || '',
    ]),
  );

  const tile = (label, value, primary = false) => (
    <Col xs={6} md={3}>
      <Card className="shadow-sm h-100 stat-tile">
        <Card.Body className="text-center">
          <h6 className="text-muted mb-2">{label}</h6>
          <h2 className={primary ? 'text-primary mb-0' : 'mb-0'}>{loading || error ? EMPTY : formatNumber(value)}</h2>
        </Card.Body>
      </Card>
    </Col>
  );

  let body;
  if (error) {
    body = <ErrorState inCard message={error} onRetry={fetchAdmins} />;
  } else if (loading) {
    body = <LoadingState label="Loading company admins" />;
  } else if (rows.length === 0) {
    body = (
      <div className="p-4 text-center text-muted">
        {admins.length === 0 ? 'No company has an admin yet' : rangeProblem ? 'The From date is after the To date' : 'No admins match these filters'}
      </div>
    );
  } else {
    body = (
      /* On phones each admin is a card titled by their name; the headcount and join date are desktop detail */
      <StackedTable responsive hover className="mb-0">
        <thead className="table-light">
          <tr>
            <SortableHeader field="name" {...sortProps}>Admin</SortableHeader>
            <SortableHeader field="email" {...sortProps}>Email</SortableHeader>
            <SortableHeader field="companyName" {...sortProps}>Company</SortableHeader>
            <SortableHeader field="plan" {...sortProps}>Plan</SortableHeader>
            <SortableHeader field="paidUsers" align="text-end" {...sortProps}>Paid seats</SortableHeader>
            <SortableHeader field="companyUsers" align="text-end d-none d-xl-table-cell" title="Users in the company, not counting deleted ones" {...sortProps}>Users</SortableHeader>
            <SortableHeader field="companyCreated" align="text-nowrap" {...sortProps}>Company created</SortableHeader>
            <SortableHeader field="adminSince" align="text-nowrap d-none d-xl-table-cell" title={ADMIN_JOINED_HINT} {...sortProps}>Admin joined</SortableHeader>
          </tr>
        </thead>
        <tbody>
          {pageRows.map((a) => (
            <tr
              key={`${a.companyId}-${a.userId}`}
              onClick={rowClick(() => navigate(`/users/${a.userId}`))}
              style={{ cursor: 'pointer' }}
              className="table-row-hover"
            >
              <td className="align-middle">
                <Link className="cell-link" to={`/users/${a.userId}`}>{fullName(a) || a.email || a.userId}</Link>
                {a.signedUpCompany && (
                  <Badge bg="light" text="dark" className="ms-2 border" title="The first person to sign this company up">First user</Badge>
                )}
              </td>
              {/* Only a phone card is too narrow for a long address; wider screens keep it on one line */}
              <td className="align-middle" style={isMobile ? { overflowWrap: 'anywhere' } : undefined}>
                {a.email ? <a href={`mailto:${a.email}`}>{a.email}</a> : EMPTY}
              </td>
              <td className="align-middle">
                <Link to={`/companies/${a.companyId}`}>{a.companyName || a.companyId}</Link>
                {!a.companyEnabled && <Badge bg="secondary" className="ms-2">Disabled</Badge>}
              </td>
              <td className="align-middle text-nowrap">{planLabel(planKey(a))}</td>
              <td className={`text-end align-middle${a.paidUsers > 0 ? '' : ' text-muted'}`}>{formatNumber(a.paidUsers ?? 0)}</td>
              <td className="text-end align-middle d-none d-xl-table-cell">{formatNumber(a.companyUsers ?? 0)}</td>
              <td className="align-middle text-nowrap">{formatDate(localDay(a.companyCreated))}</td>
              <td className="align-middle text-nowrap d-none d-xl-table-cell">{formatDate(localDay(a.adminSince))}</td>
            </tr>
          ))}
        </tbody>
      </StackedTable>
    );
  }

  return (
    <Container fluid className="py-4">
      <ReportHeader
        title="Company admins"
        description="Every company admin across every company, with the company's plan and paid seats. The tiles and the CSV follow the filters."
        actions={(
          <Button variant="outline-secondary" onClick={exportCsv} disabled={!rows.length}>
            <Download size={14} className="me-1" aria-hidden="true" />Export CSV
          </Button>
        )}
      />

      <Row className="g-3 g-md-4 mb-4">
        {tile('Admins', stats.admins, true)}
        {tile('Companies', stats.companies)}
        {tile('Paid companies', stats.paidCompanies)}
        {tile('Paid seats', stats.paidSeats)}
      </Row>

      <Card className="shadow-sm">
        <Card.Header className="bg-light d-flex align-items-center flex-wrap gap-2 gap-md-3 filter-bar">
          <Form.Control
            size="sm"
            type="search"
            placeholder="Search name, email or company"
            aria-label="Search name, email or company"
            value={search}
            onChange={(e) => resetting(setSearch)(e.target.value)}
            className="filter-bar-wide"
            style={{ maxWidth: 280 }}
          />
          <Form.Group controlId="companyAdminsDateField" className="d-flex align-items-center gap-2 ms-md-auto filter-bar-wide">
            <Form.Label className="small text-muted mb-0" style={dateLabelStyle}>Date</Form.Label>
            <Form.Select size="sm" value={dateField} onChange={(e) => resetting(setDateField)(e.target.value)} style={controlStyle} className={isMobile ? undefined : 'pe-5'}>
              {DATE_FIELDS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group controlId="companyAdminsFrom" className="d-flex align-items-center gap-2 filter-bar-wide">
            <Form.Label className="small text-muted mb-0" style={dateLabelStyle}>From</Form.Label>
            <Form.Control
              type="date"
              size="sm"
              value={from}
              max={to || undefined}
              onChange={(e) => resetting(setFrom)(e.target.value)}
              isInvalid={!!rangeProblem}
              style={controlStyle || { width: 150 }}
            />
          </Form.Group>
          <Form.Group controlId="companyAdminsTo" className="d-flex align-items-center gap-2 filter-bar-wide">
            <Form.Label className="small text-muted mb-0" style={dateLabelStyle}>To</Form.Label>
            <Form.Control
              type="date"
              size="sm"
              value={to}
              min={from || undefined}
              onChange={(e) => resetting(setTo)(e.target.value)}
              isInvalid={!!rangeProblem}
              style={controlStyle || { width: 150 }}
            />
          </Form.Group>
          <Form.Group controlId="companyAdminsPaid" className="d-flex align-items-center gap-2">
            <Form.Label className="small text-muted mb-0">Paid</Form.Label>
            <Form.Select size="sm" value={paid} onChange={(e) => resetting(setPaid)(e.target.value)} style={controlStyle} className={isMobile ? undefined : 'pe-5'}>
              <option value="all">Paid and free</option>
              <option value="paid">Paid only</option>
              <option value="free">Free only</option>
            </Form.Select>
          </Form.Group>
          <PlanFilter id="companyAdminsPlans" options={plans} hidden={hiddenPlans} onChange={resetting(setHiddenPlans)} />
          {filtersActive && (
            <Button variant="link" size="sm" className="p-0" onClick={clearFilters}>Clear filters</Button>
          )}
        </Card.Header>
        <Card.Body className="p-0">{body}</Card.Body>
        {!loading && !error && rows.length > 0 && (
          <Card.Footer className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <small className="text-muted">
              {totalPages > 1
                ? `Showing ${formatNumber((currentPage - 1) * PAGE_SIZE + 1)} to ${formatNumber(Math.min(currentPage * PAGE_SIZE, rows.length))} of ${plural(rows.length, 'admin')}`
                : plural(rows.length, 'admin')}
              {rows.length !== admins.length && ` (${formatNumber(admins.length)} in total)`}
            </small>
            {totalPages > 1 && (
              <Pagination className="mb-0">
                <Pagination.Prev disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)} />
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                  .reduce((items, p, idx, arr) => {
                    if (idx > 0 && p - arr[idx - 1] > 1) {
                      items.push(<Pagination.Ellipsis key={`ellipsis-${p}`} disabled />);
                    }
                    items.push(
                      <Pagination.Item key={p} active={p === currentPage} onClick={() => setPage(p)}>{p}</Pagination.Item>,
                    );
                    return items;
                  }, [])}
                <Pagination.Next disabled={currentPage === totalPages} onClick={() => setPage(currentPage + 1)} />
              </Pagination>
            )}
          </Card.Footer>
        )}
      </Card>
    </Container>
  );
};

export default CompanyAdmins;
